import { useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ActionDependant,
  evaluateTemplateValue,
  GlobalDataContext,
  TemplateContext,
} from '@ea-lab/reactive-json';
import { deleteDocDirectory, archiveDocDirectory, unarchiveEntity } from '../../../services/api';
import { Trash2, Archive, RotateCcw } from 'lucide-react';
import ConfirmTrashModal from '../../shared/ui/ConfirmTrashModal';
import { useDocArchived } from '../DocArchivedContext';
import { useAuth } from '../../../context/AuthContext';

export const DocSpaceActions = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const navigate = useNavigate();
  const archivedAt = useDocArchived();
  const { isAdmin } = useAuth();
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [showUnarchiveConfirm, setShowUnarchiveConfirm] = useState(false);
  const [unarchivingSpace, setUnarchivingSpace] = useState(false);

  const directoryId = evaluateTemplateValue({
    valueToEvaluate: props.directoryId,
    globalDataContext,
    templateContext,
  });

  const directoryName = evaluateTemplateValue({
    valueToEvaluate: props.directoryName,
    globalDataContext,
    templateContext,
  });

  const rawParentId = evaluateTemplateValue({
    valueToEvaluate: props.parentId,
    globalDataContext,
    templateContext,
  });

  const parentId = typeof rawParentId === 'number'
    ? rawParentId
    : parseInt(rawParentId || '0', 10);
  const isRoot = !parentId || parentId === 0;

  const typeLabel = isRoot ? 'l\'espace' : 'le répertoire';
  const name = directoryName || `#${directoryId}`;

  const handleConfirm = useCallback(async () => {
    if (!directoryId || deleting) return;
    setDeleting(true);
    try {
      await deleteDocDirectory(directoryId);
      setShowConfirm(false);
      window.dispatchEvent(new CustomEvent('doc:entity-trashed', {
        detail: { type: 'directory', id: directoryId, parentId, isRoot },
      }));
      const target = isRoot ? '/docs' : `/docs/space/${parentId}`;
      navigate(target);
    } catch (err) {
      console.error('Trash failed:', err);
    } finally {
      setDeleting(false);
    }
  }, [directoryId, isRoot, parentId, deleting, navigate]);

  const handleArchiveConfirm = useCallback(async () => {
    if (!directoryId || archiving) return;
    setArchiving(true);
    try {
      await archiveDocDirectory(directoryId);
      setShowArchiveConfirm(false);
      window.dispatchEvent(new CustomEvent('doc:entity-archived', {
        detail: { type: 'directory', id: directoryId, parentId, isRoot },
      }));
      const target = isRoot ? '/docs' : `/docs/space/${parentId}`;
      navigate(target);
    } catch (err) {
      console.error('Archive failed:', err);
      const msg = err.message?.includes('403')
        ? 'Permission refusée. Vous n\'avez pas les droits pour archiver cet élément.'
        : `Erreur : ${err.message}`;
      alert(msg);
    } finally {
      setArchiving(false);
    }
  }, [directoryId, isRoot, parentId, archiving, navigate]);

  const handleUnarchiveConfirm = useCallback(async () => {
    if (!directoryId || unarchivingSpace) return;
    setUnarchivingSpace(true);
    try {
      await unarchiveEntity('taxonomy_term', 'doc_directory', directoryId);
      setShowUnarchiveConfirm(false);
      window.location.reload();
    } catch (err) {
      console.error('Unarchive failed:', err);
      alert(`Erreur : ${err.message}`);
    } finally {
      setUnarchivingSpace(false);
    }
  }, [directoryId, unarchivingSpace]);

  const isArchived = !!archivedAt;

  return (
    <ActionDependant {...props}>
      {/* Archiver reprend le style du bouton « archivées » de la barre des
          tâches (`tasks.yaml`) : bordure grise, fond blanc, texte sourd.
          Archiver n'est pas une action colorée — c'est un rangement, pas un
          avertissement ; le bleu la mettait au même niveau visuel que
          « Supprimer », en rouge juste à côté. */}
      {!isArchived && (
        <button
          onClick={() => setShowArchiveConfirm(true)}
          className="h-8 inline-flex items-center gap-1.5 px-2.5 text-sm text-slate-500 border border-slate-200 rounded-lg bg-white whitespace-nowrap transition-colors hover:text-slate-700 hover:border-slate-300"
          aria-label="Archiver"
        >
          <Archive size={15} aria-hidden="true" />
          <span className="hidden sm:inline">Archiver</span>
        </button>
      )}

      {isArchived && isAdmin && (
        <button
          onClick={() => setShowUnarchiveConfirm(true)}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 text-green-600 hover:bg-green-50 border border-transparent hover:border-green-200 rounded-xl text-sm font-medium transition-all"
          aria-label="Désarchiver"
        >
          <RotateCcw size={14} />
          <span className="hidden sm:inline">Désarchiver</span>
        </button>
      )}

      <button
        onClick={() => setShowConfirm(true)}
        className="inline-flex items-center gap-1.5 px-3.5 py-2 text-red-500 hover:bg-red-50 border border-transparent hover:border-red-200 rounded-xl text-sm font-medium transition-all"
        aria-label="Supprimer"
      >
        <Trash2 size={14} />
        <span className="hidden sm:inline">Supprimer</span>
      </button>

      <ConfirmTrashModal
        isOpen={showConfirm}
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirm(false)}
        title={`Supprimer ${typeLabel} « ${name} » ?`}
        description={`${isRoot ? 'Cet espace' : 'Ce répertoire'} et tout son contenu seront déplacés dans la corbeille. Vous pourrez les restaurer pendant 90 jours.`}
        confirmLabel="Déplacer dans la corbeille"
        loading={deleting}
      />

      {!isArchived && (
        <ConfirmTrashModal
          isOpen={showArchiveConfirm}
          onConfirm={handleArchiveConfirm}
          onCancel={() => setShowArchiveConfirm(false)}
          title={`Archiver ${typeLabel} « ${name} » ?`}
          description={`${isRoot ? 'Cet espace' : 'Ce répertoire'} et tout son contenu seront archivés en lecture seule.`}
          confirmLabel="Archiver"
          loading={archiving}
          variant="archive"
        />
      )}

      {isArchived && (
        <ConfirmTrashModal
          isOpen={showUnarchiveConfirm}
          onConfirm={handleUnarchiveConfirm}
          onCancel={() => setShowUnarchiveConfirm(false)}
          title={`Désarchiver ${typeLabel} « ${name} » ?`}
          description={`${isRoot ? 'Cet espace' : 'Ce répertoire'} et tout son contenu redeviendront actifs et modifiables.`}
          confirmLabel="Désarchiver"
          loading={unarchivingSpace}
        />
      )}
    </ActionDependant>
  );
};
